import { jsx as _jsx } from "react/jsx-runtime";
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, redirect } from "react-router-dom";
import { me, refresh } from "@/api/auth";
import { AppLayout } from "@/components/layout/AppLayout";
import { RouteLoading } from "@/components/RouteLoading";
import { canManageHr, hasOrgStructure } from "@/lib/access";
import { useAuth } from "@/store/auth";
// ---------------------------------------------------------------------------
// Lazy-loaded страницы. Каждая — свой chunk в результирующем bundle.
// Vite делит по границам динамических импортов + общий vendor-chunk для
// React/router/axios. Named → default конверсия через `.then(m => …)`.
// ---------------------------------------------------------------------------
const LoginPage = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const InvitationAcceptPage = lazy(() => import("@/pages/InvitationAcceptPage").then((m) => ({
    default: m.InvitationAcceptPage,
})));
const RegisterPage = lazy(() => import("@/pages/RegisterPage").then((m) => ({ default: m.RegisterPage })));
const PasswordResetRequestPage = lazy(() => import("@/pages/PasswordResetRequestPage").then((m) => ({
    default: m.PasswordResetRequestPage,
})));
const PasswordResetPage = lazy(() => import("@/pages/PasswordResetPage").then((m) => ({
    default: m.PasswordResetPage,
})));
const EmailCancelPage = lazy(() => import("@/pages/EmailCancelPage").then((m) => ({
    default: m.EmailCancelPage,
})));
const InstanceRedirectPage = lazy(() => import("@/pages/InstanceRedirectPage").then((m) => ({
    default: m.InstanceRedirectPage,
})));
const MePage = lazy(() => import("@/pages/MePage").then((m) => ({ default: m.MePage })));
const ProductsPage = lazy(() => import("@/pages/ProductsPage").then((m) => ({ default: m.ProductsPage })));
const EmployeesPage = lazy(() => import("@/pages/admin/EmployeesPage").then((m) => ({
    default: m.EmployeesPage,
})));
const TenantsPage = lazy(() => import("@/pages/admin/TenantsPage").then((m) => ({ default: m.TenantsPage })));
const OrgPage = lazy(() => import("@/pages/admin/OrgPage").then((m) => ({ default: m.OrgPage })));
const SitesPage = lazy(() => import("@/pages/admin/SitesPage").then((m) => ({ default: m.SitesPage })));
const AuditPage = lazy(() => import("@/pages/admin/AuditPage").then((m) => ({ default: m.AuditPage })));
const AnalyticsPage = lazy(() => import("@/pages/admin/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })));
const EmailTemplatesPage = lazy(() => import("@/pages/admin/EmailTemplatesPage").then((m) => ({
    default: m.EmailTemplatesPage,
})));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })));
/**
 * Редирект на /login с сохранением исходного пути в `?next=` — иначе админ,
 * пришедший разлогиненным по ссылке из письма (например
 * /admin/employees?tab=registrations), после входа падал бы на /me и терял
 * цель. Только внутренние same-origin пути (`/...`, не `//...`) — от
 * open-redirect; LoginPage валидирует зеркально.
 */
function loginRedirect(request) {
    // prompt=login: requireAuth попадает сюда ПОСЛЕ собственного провального
    // refresh — повторная тихая попытка от LoginPage была бы заведомо мёртвым
    // round-trip'ом (двойной спиннер на самом частом пути к форме).
    if (!request)
        return "/login?prompt=login";
    const url = new URL(request.url);
    const next = url.pathname + url.search;
    if (!next.startsWith("/") || next.startsWith("//") || next === "/") {
        return "/login?prompt=login";
    }
    return `/login?prompt=login&next=${encodeURIComponent(next)}`;
}
async function requireAuth(args) {
    if (useAuth.getState().tokens)
        return null;
    // Try to resurrect the session from the refresh cookie.
    try {
        await refresh();
        return null;
    }
    catch {
        throw redirect(loginRedirect(args?.request));
    }
}
/**
 * Loader для admin-only роутов. Сначала прогоняет requireAuth (refresh
 * cookie / валидный access), затем читает /me и проверяет
 * `product_roles.signaris_auth in {admin, superadmin}`. Без этого guard'а
 * случайный pаспоряжение URL'ом приводит к пустой странице (сервер
 * 403'ит API, UI получает «нет данных»).
 */
async function requireAdmin(args) {
    await requireAuth(args);
    try {
        const profile = await me();
        const role = profile.product_roles?.signaris_auth;
        if (role !== "admin" && role !== "superadmin") {
            throw redirect("/me");
        }
    }
    catch (e) {
        // redirect-ошибки react-router сам обрабатывает throw'ом — пропускаем.
        if (e instanceof Response)
            throw e;
        throw redirect("/me");
    }
    return null;
}
/**
 * Сотрудники и оргструктура: админ организации, суперадмин или кадровик
 * (`signaris_auth:hr`). Оргструктура — только организациям с Hub.
 */
async function requireHrManager(args) {
    await requireAuth(args);
    try {
        const profile = await me();
        if (!canManageHr(profile))
            throw redirect("/me");
    }
    catch (e) {
        if (e instanceof Response)
            throw e;
        throw redirect("/me");
    }
    return null;
}
async function requireOrgStructure(args) {
    await requireAuth(args);
    try {
        const profile = await me();
        if (!hasOrgStructure(profile))
            throw redirect("/me");
    }
    catch (e) {
        if (e instanceof Response)
            throw e;
        throw redirect("/me");
    }
    return null;
}
/** Аналогично, но требует именно superadmin (для /admin/tenants). */
async function requireSuperadmin(args) {
    await requireAuth(args);
    try {
        const profile = await me();
        if (profile.product_roles?.signaris_auth !== "superadmin") {
            throw redirect("/me");
        }
    }
    catch (e) {
        if (e instanceof Response)
            throw e;
        throw redirect("/me");
    }
    return null;
}
function suspended(children) {
    return _jsx(Suspense, { fallback: _jsx(RouteLoading, {}), children: children });
}
export const router = createBrowserRouter([
    { path: "/", element: _jsx(Navigate, { to: "/me", replace: true }) },
    { path: "/login", element: suspended(_jsx(LoginPage, {})) },
    { path: "/register", element: suspended(_jsx(RegisterPage, {})) },
    // Tenant-ссылка регистрации: slug в пути предзаполняет «Код организации»
    // (uppetit.signaris.ru 302-ит сюда). Тот же компонент, без loader'а.
    { path: "/register/:orgCode", element: suspended(_jsx(RegisterPage, {})) },
    { path: "/invitation/accept", element: suspended(_jsx(InvitationAcceptPage, {})) },
    { path: "/password/reset/request", element: suspended(_jsx(PasswordResetRequestPage, {})) },
    { path: "/password/reset", element: suspended(_jsx(PasswordResetPage, {})) },
    // Публичная намеренно: отмену открывает владелец старого адреса, у которого
    // сессии может не быть вовсе. Подтверждение — кодом в профиле (EmailChangeDialog),
    // маршрута /email/confirm нет с 19.09.2026.
    { path: "/email/cancel", element: suspended(_jsx(EmailCancelPage, {})) },
    {
        // Отказ instance-routing'а на /sso/authorize (см. InstanceRedirectPage).
        // Standalone (без AppLayout-шелла), но требует сессию — URL правильного
        // инстанса страница берёт из /auth/me.
        path: "/instance",
        element: suspended(_jsx(InstanceRedirectPage, {})),
        loader: requireAuth,
    },
    {
        // AppLayout — shell с навигацией; сам не lazy (мало, нужен сразу).
        // Дочерние страницы грузятся по запросу; на время загрузки — локальный
        // spinner в контентной области, шапка и меню остаются видимыми.
        element: _jsx(AppLayout, {}),
        loader: requireAuth,
        children: [
            { path: "/me", element: suspended(_jsx(MePage, {})) },
            { path: "/products", element: suspended(_jsx(ProductsPage, {})) },
            {
                path: "/admin/employees",
                element: suspended(_jsx(EmployeesPage, {})),
                loader: requireHrManager,
            },
            {
                path: "/admin/org",
                element: suspended(_jsx(OrgPage, {})),
                loader: requireOrgStructure,
            },
            {
                path: "/admin/tenants",
                element: suspended(_jsx(TenantsPage, {})),
                loader: requireSuperadmin,
            },
            {
                path: "/admin/sites",
                element: suspended(_jsx(SitesPage, {})),
                loader: requireAdmin,
            },
            {
                path: "/admin/audit",
                element: suspended(_jsx(AuditPage, {})),
                loader: requireAdmin,
            },
            {
                path: "/admin/analytics",
                element: suspended(_jsx(AnalyticsPage, {})),
                loader: requireAdmin,
            },
            {
                path: "/admin/email-templates",
                element: suspended(_jsx(EmailTemplatesPage, {})),
                loader: requireAdmin,
            },
        ],
    },
    { path: "*", element: suspended(_jsx(NotFoundPage, {})) },
]);
